import { c as _c } from "react/compiler-runtime";
import { Alert, Avatar, Badge, Button, Field, HStack, Input, Skeleton, Text, VStack } from '@chakra-ui/react';
import { ClarityLinkCodeStep, ClarityLinkCodeStepProps } from '@src/components/sco/Advisors/clarityLink/ClarityLinkCodeStep';
import { CLARITY_SUPPORT_EMAIL } from '@src/components/sco/Advisors/clarityLink/clarityLinkHelpers';
import { TextLink } from '@src/components/sco/TextLink';
import { ScoClarityAdvisorImportClaimStatus, ScoClarityLinkProofMethod } from '@src/graphql-types';
import { FormEvent, ReactNode } from 'react';
import { LuRefreshCw } from 'react-icons/lu';
export type ClarityAccountExpert = {
  fullName?: string | null;
  screenName?: string | null;
  imageUrl?: string | null;
  shortBio?: string | null;
  clarityProfileUrl?: string | null;
};
export type ClarityAccountCandidate = {
  claimStatus: ScoClarityAdvisorImportClaimStatus;
  proofMethod?: ScoClarityLinkProofMethod | null;
  expert: ClarityAccountExpert | null;
};
export type ClarityAccountCardViewProps = {
  loading?: boolean;
  /** The member's linked Clarity account. `expert` is null while it loads or if Clarity is down. */
  linked?: {
    expert: ClarityAccountExpert | null;
  } | null;
  onSync?: () => void;
  syncing?: boolean;
  handleInput?: string;
  onHandleInputChange?: (value: string) => void;
  onFind?: () => void;
  finding?: boolean;
  candidate?: ClarityAccountCandidate | null;
  onLink?: () => void;
  linking?: boolean;
  codeStep?: ClarityLinkCodeStepProps | null;
  error?: string | null;
  notice?: string | null;
};
const ExpertRow = t0 => {
  const $ = _c(25);
  const {
    expert,
    badge
  } = t0;
  const name = expert.fullName || expert.screenName || "Clarity expert";
  let t1;
  if ($[0] !== name) {
    t1 = <Avatar.Fallback name={name} />;
    $[0] = name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== expert.imageUrl || $[3] !== name) {
    t2 = expert.imageUrl ? <Avatar.Image alt={name} src={expert.imageUrl} /> : null;
    $[2] = expert.imageUrl;
    $[3] = name;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <Avatar.Root flexShrink={0} size="md">{t1}{t2}</Avatar.Root>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== name) {
    t4 = <Text fontWeight="medium" textStyle="sm">{name}</Text>;
    $[8] = name;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== badge || $[11] !== t4) {
    t5 = <HStack gap={2} wrap="wrap">{t4}{badge}</HStack>;
    $[10] = badge;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== expert.shortBio) {
    t6 = expert.shortBio && expert.shortBio.toLowerCase() !== "clarity expert" ? <Text color="fg.muted" textStyle="xs">{expert.shortBio}</Text> : null;
    $[13] = expert.shortBio;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== expert.clarityProfileUrl || $[16] !== expert.screenName) {
    t7 = expert.clarityProfileUrl ? <TextLink href={expert.clarityProfileUrl} target="_blank" textStyle="xs" width="fit-content">clarity.fm/{expert.screenName}</TextLink> : null;
    $[15] = expert.clarityProfileUrl;
    $[16] = expert.screenName;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t5 || $[19] !== t6 || $[20] !== t7) {
    t8 = <VStack align="flex-start" gap={0.5} minWidth={0}>{t5}{t6}{t7}</VStack>;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t3 || $[23] !== t8) {
    t9 = <HStack align="start" gap={3}>{t3}{t8}</HStack>;
    $[22] = t3;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
};
const candidateBlocker = (candidate: ClarityAccountCandidate): string | null => {
  switch (candidate.claimStatus) {
    case ScoClarityAdvisorImportClaimStatus.NotFound:
      return 'No Clarity expert found with that username.';
    case ScoClarityAdvisorImportClaimStatus.NotExpert:
      return "That Clarity account isn't set up as an expert.";
    case ScoClarityAdvisorImportClaimStatus.ClaimedByOther:
      return `This Clarity account is linked to another Startups.com account. Contact ${CLARITY_SUPPORT_EMAIL} if it's yours.`;
    default:
      break;
  }
  if (candidate.proofMethod === ScoClarityLinkProofMethod.AdminOnly) {
    return `We can't verify this Clarity account by email. Contact ${CLARITY_SUPPORT_EMAIL} to link it.`;
  }
  return null;
};

/**
 * Advisor profile › Settings › Clarity.fm account (Clarity link plan D1). Linked: who, and Sync
 * from Clarity. Not linked: find the account, then link it by matching email or an emailed code.
 * Unlinking is admin-only (D7).
 */
export const ClarityAccountCardView = t0 => {
  const $ = _c(46);
  const {
    loading: t1,
    linked: t2,
    onSync,
    syncing: t3,
    handleInput: t4,
    onHandleInputChange,
    onFind,
    finding: t5,
    candidate: t6,
    onLink,
    linking: t7,
    codeStep: t8,
    error: t9,
    notice: t10
  } = t0;
  const loading = t1 === undefined ? false : t1;
  const linked = t2 === undefined ? null : t2;
  const syncing = t3 === undefined ? false : t3;
  const handleInput = t4 === undefined ? "" : t4;
  const finding = t5 === undefined ? false : t5;
  const candidate = t6 === undefined ? null : t6;
  const linking = t7 === undefined ? false : t7;
  const codeStep = t8 === undefined ? null : t8;
  const error = t9 === undefined ? null : t9;
  const notice = t10 === undefined ? null : t10;
  let t11;
  if ($[0] !== notice) {
    t11 = notice ? <Alert.Root size="sm" status="success"><Alert.Indicator /><Alert.Title>{notice}</Alert.Title></Alert.Root> : null;
    $[0] = notice;
    $[1] = t11;
  } else {
    t11 = $[1];
  }
  let t12;
  if ($[2] !== error) {
    t12 = error ? <Alert.Root size="sm" status="error"><Alert.Indicator /><Alert.Title>{error}</Alert.Title></Alert.Root> : null;
    $[2] = error;
    $[3] = t12;
  } else {
    t12 = $[3];
  }
  let t13;
  if ($[4] !== t11 || $[5] !== t12) {
    t13 = <>{t11}{t12}</>;
    $[4] = t11;
    $[5] = t12;
    $[6] = t13;
  } else {
    t13 = $[6];
  }
  const status = t13;
  if (loading) {
    let t14;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <Skeleton height="4.5rem" rounded="l2" />;
      $[7] = t14;
    } else {
      t14 = $[7];
    }
    return t14;
  }
  if (linked) {
    let t14;
    if ($[8] !== linked.expert) {
      t14 = linked.expert ? <ExpertRow expert={linked.expert} badge={<Badge colorPalette="teal" variant="subtle">Linked</Badge>} /> : <HStack gap={2}><Badge colorPalette="teal" variant="subtle">Linked</Badge><Text color="fg.muted" textStyle="sm">Your Clarity.fm account is linked.</Text></HStack>;
      $[8] = linked.expert;
      $[9] = t14;
    } else {
      t14 = $[9];
    }
    let t15;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <Text color="fg.muted" fontFamily="longform" textStyle="sm">Your Clarity reviews show on your profile next to your Startups reviews. To change or remove this link, contact {CLARITY_SUPPORT_EMAIL}.</Text>;
      $[10] = t15;
    } else {
      t15 = $[10];
    }
    let t16;
    if ($[11] !== onSync || $[12] !== syncing) {
      t16 = onSync ? <HStack><Button loading={syncing} size="sm" variant="outline" onClick={onSync}><LuRefreshCw />Sync from Clarity</Button></HStack> : null;
      $[11] = onSync;
      $[12] = syncing;
      $[13] = t16;
    } else {
      t16 = $[13];
    }
    let t17;
    if ($[14] !== status || $[15] !== t14 || $[16] !== t16) {
      t17 = <VStack align="stretch" gap={4}>{t14}{t15}{t16}{status}</VStack>;
      $[14] = status;
      $[15] = t14;
      $[16] = t16;
      $[17] = t17;
    } else {
      t17 = $[17];
    }
    return t17;
  }
  let t14;
  if ($[18] !== candidate) {
    t14 = candidate ? candidateBlocker(candidate) : null;
    $[18] = candidate;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  const blocker = t14;
  const canLink = !!candidate && !blocker && !!onLink;
  let t15;
  if ($[20] !== handleInput || $[21] !== onFind) {
    t15 = event => {
      event.preventDefault();
      if (handleInput.trim() && onFind) {
        onFind();
      }
    };
    $[20] = handleInput;
    $[21] = onFind;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  const submitFind = t15;
  let t16;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Text color="fg.muted" fontFamily="longform" textStyle="sm">Advise on Clarity.fm? Link your expert account to bring your Clarity reviews and rating to your profile.</Text>;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== codeStep || $[25] !== finding || $[26] !== handleInput || $[27] !== onHandleInputChange || $[28] !== submitFind) {
    t17 = codeStep ? null : <form onSubmit={submitFind}><HStack align="end" gap={2} wrap="wrap"><Field.Root flex={1} minWidth="14rem"><Field.Label>Clarity username or profile link</Field.Label><Input placeholder="clarity.fm/yourname" value={handleInput} onChange={event_0 => onHandleInputChange?.(event_0.target.value)} /></Field.Root><Button disabled={!handleInput.trim()} loading={finding} type="submit" variant="outline">Find</Button></HStack></form>;
    $[24] = codeStep;
    $[25] = finding;
    $[26] = handleInput;
    $[27] = onHandleInputChange;
    $[28] = submitFind;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== candidate) {
    t18 = candidate?.expert ? <ExpertRow expert={candidate.expert} /> : null;
    $[30] = candidate;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== blocker) {
    t19 = blocker ? <Text color="fg.muted" textStyle="sm">{blocker}</Text> : null;
    $[32] = blocker;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== canLink || $[35] !== candidate?.proofMethod || $[36] !== codeStep || $[37] !== linking || $[38] !== onLink) {
    t20 = codeStep ? <ClarityLinkCodeStep {...codeStep} /> : canLink ? <VStack align="stretch" gap={2}>{candidate?.proofMethod === ScoClarityLinkProofMethod.EmailCode ? <Text color="fg.muted" textStyle="sm">To confirm it's yours, we'll email a code to the address on this Clarity account.</Text> : null}<HStack><Button loading={linking} variant="cta" onClick={onLink}>{candidate?.proofMethod === ScoClarityLinkProofMethod.EmailCode ? "Email me a code" : "Link this account"}</Button></HStack></VStack> : null;
    $[34] = canLink;
    $[35] = candidate?.proofMethod;
    $[36] = codeStep;
    $[37] = linking;
    $[38] = onLink;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== status || $[41] !== t17 || $[42] !== t18 || $[43] !== t19 || $[44] !== t20) {
    t21 = <VStack align="stretch" gap={4}>{t16}{t17}{t18}{t19}{t20}{status}</VStack>;
    $[40] = status;
    $[41] = t17;
    $[42] = t18;
    $[43] = t19;
    $[44] = t20;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  return t21;
};